"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import AlertDialog from "./AlertDialog";
import {
  TrashIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  UserCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1e2430",
    borderColor: state.isFocused ? "#f97316" : "#2d3748",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    borderRadius: "0.5rem",
    padding: "0.1rem 0.25rem",
    minHeight: "38px",
    fontSize: "13px",
    color: "#e2e8f0",
  }),
  input: (p) => ({ ...p, color: "#e2e8f0", margin: 0, padding: 0 }),
  singleValue: (p) => ({ ...p, color: "#e2e8f0" }),
  placeholder: (p) => ({ ...p, color: "#64748b" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected ? "#2d3748" : "#1e2430",
    color: state.isSelected ? "#f97316" : "#e2e8f0",
    "&:hover": { backgroundColor: "#2d3748" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#1e2430",
    borderRadius: "0.5rem",
    border: "1px solid #2d3748",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
  }),
  valueContainer: (b) => ({ ...b, padding: "0 6px" }),
  indicatorsContainer: (b) => ({ ...b, padding: "0" }),
  dropdownIndicator: (b) => ({
    ...b,
    padding: "4px",
    color: "#64748b",
    "&:hover": { color: "#e2e8f0" },
  }),
  clearIndicator: (b) => ({
    ...b,
    color: "#64748b",
    "&:hover": { color: "#f97316" },
  }),
};

const INPUT_CLS =
  "w-full rounded-lg bg-[#1e2430] border border-[#2d3748] text-slate-200 text-sm px-3 py-2 placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition";

function Avatar({ firstName, lastName }) {
  const initials =
    `${(firstName || "?")[0]}${(lastName || "")[0] || ""}`.toUpperCase();
  const colors = [
    "#b45309",
    "#0f766e",
    "#1d4ed8",
    "#7c3aed",
    "#be123c",
    "#0369a1",
  ];
  const idx = (firstName?.charCodeAt(0) || 0) % colors.length;
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
      style={{ backgroundColor: colors[idx] }}
    >
      {initials}
    </div>
  );
}

export default function ContactsView() {
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const formRef = useRef(null);

  function emptyForm() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      address: {
        formattedAddress: "",
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        latitude: null,
        longitude: null,
      },
      relatedAccount: null,
      phoneNumber: "",
    };
  }

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([fetchContacts(), fetchAccounts()]);
      setIsLoading(false);
    })();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/contacts",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setContacts(
        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
      );
    } catch {
      showAlert("error", "Error", "Failed to load contacts.");
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/accounts",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccounts(data);
    } catch {
      showAlert("error", "Error", "Failed to load accounts.");
    }
  };

  const showAlert = (type, title, message) =>
    setAlert({ open: true, type, title, message });

  const closeAlert = () => setAlert((p) => ({ ...p, open: false }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((p) => ({ ...p, address: { ...p.address, [field]: value } }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handlePlaceSelect = async (place) => {
    if (!place?.value) return;
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg`,
      );
      const json = await res.json();
      if (json.status === "OK" && json.results.length > 0) {
        const result = json.results[0];
        const { lat, lng } = result.geometry.location;
        let street = "",
          city = "",
          state = "",
          zip = "",
          country = "";
        result.address_components.forEach((c) => {
          if (c.types.includes("street_number") || c.types.includes("route"))
            street += (street ? " " : "") + c.long_name;
          if (c.types.includes("locality")) city = c.long_name;
          if (c.types.includes("administrative_area_level_1"))
            state = c.short_name;
          if (c.types.includes("postal_code")) zip = c.long_name;
          if (c.types.includes("country")) country = c.long_name;
        });
        setFormData((p) => ({
          ...p,
          address: {
            formattedAddress: place.label,
            street,
            city,
            state,
            zip,
            country,
            latitude: lat,
            longitude: lng,
          },
        }));
      }
    } catch (err) {
      showAlert("error", "Error", "Geocoding failed: " + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (editingContact) {
        await axios.put(
          `https://erp-production-72da01c8e651.herokuapp.com/api/contacts/${editingContact._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setEditingContact(null);
        showAlert("success", "Saved", "Contact updated successfully.");
      } else {
        await axios.post(
          "https://erp-production-72da01c8e651.herokuapp.com/api/contacts",
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setIsAdding(false);
        showAlert("success", "Created", "Contact added successfully.");
      }
      setFormData(emptyForm());
      fetchContacts();
      setCurrentPage(1);
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save contact.",
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/contacts/${deleteTarget._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDeleteTarget(null);
      showAlert("success", "Deleted", "Contact removed.");
      fetchContacts();
    } catch {
      showAlert("error", "Error", "Failed to delete contact.");
    }
  };

  const editContact = (contact) => {
    setEditingContact(contact);
    setIsAdding(false);
    setFormData({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      address: {
        formattedAddress: contact.address?.formattedAddress || "",
        street: contact.address?.street || "",
        city: contact.address?.city || "",
        state: contact.address?.state || "",
        zip: contact.address?.zip || "",
        country: contact.address?.country || "",
        latitude: contact.address?.latitude || null,
        longitude: contact.address?.longitude || null,
      },
      relatedAccount:
        contact.relatedAccount?.$oid || contact.relatedAccount || null,
      phoneNumber: contact.phoneNumber || "",
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openAdd = () => {
    setIsAdding(true);
    setEditingContact(null);
    setFormData(emptyForm());
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingContact(null);
  };

  const accountOptions = accounts.map((a) => ({
    value: a._id,
    label: a.companyName,
  }));

  const filteredContacts = contacts.filter((c) => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return true;
    const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
    const email = (c.email || "").toLowerCase();
    const phone = (c.phoneNumber || "").toLowerCase();
    const acc = accounts.find(
      (a) => a._id === (c.relatedAccount?.$oid || c.relatedAccount),
    );
    const accName = (acc?.companyName || "").toLowerCase();
    return (
      fullName.includes(s) ||
      email.includes(s) ||
      phone.includes(s) ||
      accName.includes(s)
    );
  });

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentContacts = filteredContacts.slice(
    indexOfFirst,
    indexOfFirst + itemsPerPage,
  );

  const paginate = (n) => {
    if (n >= 1 && n <= totalPages) setCurrentPage(n);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading contacts…</p>
        </div>
      </div>
    );
  }

  const showForm = isAdding || !!editingContact;

  return (
    <div className="min-h-screen  text-slate-200 p-6 space-y-6">
      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-orange-500 tracking-tight">
            Contacts
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {contacts.length} total contacts
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-400 text-white rounded-lg text-sm font-medium transition shadow-lg shadow-orange-900/30"
          >
            <PlusIcon className="w-4 h-4" />
            Add Contact
          </button>
        )}
      </div>

      {/* ── FORM PANEL ── */}
      {showForm && (
        <div
          ref={formRef}
          className="rounded-xl border border-[#2d3748] bg-[#161b27] shadow-2xl overflow-hidden"
        >
          {/* form header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#2d3748] bg-[#1a2035]">
            <h2 className="text-base font-semibold text-white">
              {editingContact ? "Edit Contact" : "New Contact"}
            </h2>
            <button
              onClick={cancelForm}
              className="text-slate-500 hover:text-white transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  First Name
                </label>
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Jane"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Last Name
                </label>
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Email
                </label>
                <input
                  className={INPUT_CLS}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="jane@example.com"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Phone
                </label>
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            {/* Account */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Related Account
              </label>
              <Select
                options={accountOptions}
                value={
                  accountOptions.find(
                    (o) => o.value === formData.relatedAccount,
                  ) || null
                }
                onChange={(opt) =>
                  setFormData((p) => ({
                    ...p,
                    relatedAccount: opt?.value || null,
                  }))
                }
                placeholder="Search account…"
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Address autocomplete */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Address Lookup
              </label>
              <GooglePlacesAutocomplete
                autocompletionRequest={{
                  componentRestrictions: { country: ["ca"] },
                }}
                apiKey="AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg"
                selectProps={{
                  onChange: handlePlaceSelect,
                  placeholder: "Search address…",
                  value: formData.address.formattedAddress
                    ? {
                        label: formData.address.formattedAddress,
                        value: formData.address.formattedAddress,
                      }
                    : null,
                  styles: selectStyles,
                }}
              />
            </div>

            {/* Address fields */}
            <div className="grid grid-cols-1 gap-3 pt-1 pl-3 border-l-2 border-[#2d3748]">
              <input
                className={INPUT_CLS}
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleInputChange}
                placeholder="Street"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  placeholder="City"
                />
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  placeholder="Province"
                />
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.zip"
                  value={formData.address.zip}
                  onChange={handleInputChange}
                  placeholder="Postal code"
                />
              </div>
              <input
                className={INPUT_CLS}
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleInputChange}
                placeholder="Country"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={cancelForm}
                className="px-4 py-2 text-sm rounded-lg bg-[#2d3748] text-slate-300 hover:bg-[#374151] transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-sm font-medium rounded-lg bg-orange-500 hover:bg-orange-400 text-white transition shadow-lg shadow-orange-900/20"
              >
                {editingContact ? "Save Changes" : "Create Contact"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── TABLE PANEL ── */}
      <div className="rounded-xl border border-[#2d3748] bg-[#161b27] overflow-hidden shadow-xl">
        {/* toolbar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2d3748] gap-4">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search name, email, phone, account…"
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-[#1e2430] border border-[#2d3748] text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition"
            />
          </div>
          {!showForm && (
            <button
              onClick={openAdd}
              className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-400 text-white rounded-lg text-sm font-medium transition"
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          )}
        </div>

        {filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <UserCircleIcon className="w-12 h-12 opacity-30" />
            <p className="text-sm">
              {searchTerm
                ? "No contacts match your search."
                : "No contacts yet. Add one above."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#2d3748] bg-[#1a2035]">
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e2430]">
                  {currentContacts.map((contact) => {
                    const acc = accounts.find(
                      (a) =>
                        a._id ===
                        (contact.relatedAccount?.$oid ||
                          contact.relatedAccount),
                    );
                    return (
                      <tr
                        key={contact._id}
                        className="hover:bg-[#1e2430] transition-colors group"
                      >
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <Avatar
                              firstName={contact.firstName}
                              lastName={contact.lastName}
                            />
                            <span className="font-medium text-slate-200">
                              {contact.firstName} {contact.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-slate-400">
                          {contact.email || (
                            <span className="text-slate-600 italic">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-slate-400">
                          {contact.phoneNumber || (
                            <span className="text-slate-600 italic">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {acc ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#1e3a5f] text-blue-300 border border-blue-900/40">
                              {acc.companyName}
                            </span>
                          ) : (
                            <span className="text-slate-600 italic text-xs">
                              None
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 text-xs">
                          {new Date(contact.createdAt).toLocaleDateString(
                            "en-CA",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => editContact(contact)}
                              title="Edit"
                              className="p-1.5 rounded-md text-slate-400 hover:text-orange-400 hover:bg-[#2d3748] transition"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(contact)}
                              title="Delete"
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-[#2d3748] transition"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#2d3748] bg-[#1a2035]">
                <span className="text-xs text-slate-500">
                  {indexOfFirst + 1}–
                  {Math.min(
                    indexOfFirst + itemsPerPage,
                    filteredContacts.length,
                  )}{" "}
                  of {filteredContacts.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-[#2d3748] disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 px-1">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-[#2d3748] disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── DELETE CONFIRM DIALOG ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#161b27] border border-[#2d3748] rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-900/40 border border-red-800 flex items-center justify-center">
                <TrashIcon className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">
                  Delete Contact
                </h3>
                <p className="text-slate-400 text-xs">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-slate-300 text-sm mb-5">
              Delete{" "}
              <span className="font-medium text-white">
                {deleteTarget.firstName} {deleteTarget.lastName}
              </span>
              ?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm rounded-lg bg-[#2d3748] text-slate-300 hover:bg-[#374151] transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-500 text-white transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
        onConfirm={closeAlert}
        confirmText="OK"
        cancelText="Close"
      />
    </div>
  );
}
